import {
  CHAT_ALERT_CONTENT_CLASS_NAME,
  CHAT_DIALOG_HEADER_CLASS_NAME,
  CHAT_DIALOG_DESCRIPTION_CLASS_NAME,
  CHAT_DIALOG_FOOTER_CLASS_NAME,
} from "../chat-dialog-styles";
import type { AdminChatThread } from "../types";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowLeft, Ban, Clock, MoreVertical } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/ajax/queryClient";
import { useTRPC } from "@/trpc";

export const AdminChatThreadHeader = ({
  thread,
  onBack,
}: {
  thread: AdminChatThread;
  onBack: () => void;
}) => {
  const [showStopDialog, setShowStopDialog] = useState(false);
  const trpc = useTRPC();
  const deleteChatMutation = useMutation(
    trpc.chat.adminDeleteChatThread.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["adminChatThreads"] });
        setShowStopDialog(false);
      },
    }),
  );

  const organizationParticipants = thread.organization_participants
    .map((participant) => {
      const label =
        participant.name?.trim() ??
        participant.organization_name?.trim() ??
        "Unknown organization";
      return {
        id: `organization-${participant.id}`,
        label,
        type: "organization" as const,
      };
    })
    .filter((participant) => participant.label.length > 0);

  const userParticipants = thread.user_participants
    .map((participant) => {
      const fullName =
        `${participant.first_name ?? ""} ${participant.last_name ?? ""}`
          .replace(/\s+/g, " ")
          .trim();
      return {
        id: `user-${participant.id}`,
        label: fullName || "Unknown user",
        type: "user" as const,
      };
    })
    .filter((participant) => participant.label.length > 0);

  const participants = [...organizationParticipants, ...userParticipants];

  const participantDescriptor = participants.map((participant) => {
    const typeLabel =
      participant.type === "organization" ? "Organization" : "User";
    return `${participant.label} (${typeLabel})`;
  });

  const headingText =
    participantDescriptor.length === 0
      ? "Conversation"
      : participantDescriptor.length === 1
        ? `Conversation with ${participantDescriptor[0]}`
        : `Conversation between ${participantDescriptor.join(" / ")}`;

  const avatarFallback = (() => {
    const sourceLabel = participants[0]?.label ?? "";
    const initials = sourceLabel
      .split(/\s+/)
      .map((word) => word[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
    return initials || "?";
  })();

  const participantCountSummary =
    participants.length > 0
      ? [
          `${participants.length} total`,
          organizationParticipants.length
            ? `${organizationParticipants.length} organization${organizationParticipants.length === 1 ? "" : "s"}`
            : null,
          userParticipants.length
            ? `${userParticipants.length} user${userParticipants.length === 1 ? "" : "s"}`
            : null,
        ]
          .filter(Boolean)
          .join(" / ")
      : "";

  return (
    <div className="max-h-[45dvh] min-w-0 shrink-0 overflow-y-auto border-b border-slate-200 bg-white">
      <div className="flex min-w-0 items-start gap-2 p-3 sm:gap-3 sm:p-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="h-12 w-12 shrink-0 rounded-xl text-slate-600 md:hidden"
          aria-label="Back to conversations"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <Avatar className="hidden h-10 w-10 shrink-0 rounded-xl border border-primary/15 lg:flex">
          <AvatarFallback className="rounded-xl bg-primary/10 text-primary">
            {avatarFallback}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-col gap-2">
            <div className="flex w-full items-start gap-3">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                <h2 className="min-w-0 text-base font-semibold leading-6 text-slate-900 [overflow-wrap:anywhere] sm:text-lg">
                  {headingText}
                </h2>
                {thread.archived_at && (
                  <Badge
                    variant="outline"
                    className="border-slate-200 bg-slate-50"
                  >
                    Archived
                  </Badge>
                )}
              </div>
              <div className="flex items-center">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-12 w-12 shrink-0 rounded-xl text-slate-600 hover:bg-slate-100"
                      aria-label="Conversation options"
                    >
                      <MoreVertical className="h-5 w-5 text-slate-500" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    collisionPadding={16}
                    className="max-h-[var(--radix-dropdown-menu-content-available-height)] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border-slate-200 bg-white p-1.5"
                  >
                    <DropdownMenuItem
                      onClick={() => setShowStopDialog(true)}
                      disabled={deleteChatMutation.isPending}
                      className="flex min-h-12 items-center gap-2 rounded-lg text-red-700 focus:bg-red-50 focus:text-red-700"
                    >
                      <Ban className="h-4 w-4" />
                      {deleteChatMutation.isPending
                        ? "Stopping..."
                        : "Stop Chat"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            {participants.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                {participants.map((participant) => {
                  const typeLabel =
                    participant.type === "organization"
                      ? "Organization"
                      : "User";
                  return (
                    <div
                      key={participant.id}
                      className="flex max-w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs leading-5 text-slate-600"
                    >
                      <span className="text-xs text-slate-500">
                        {typeLabel}
                      </span>
                      <span className="min-w-0 font-medium text-slate-900 [overflow-wrap:anywhere]">
                        {participant.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                Participants unavailable for this conversation.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              <span>
                {thread.last_message_time
                  ? `Updated ${formatDistanceToNow(
                      new Date(thread.last_message_time),
                      {
                        addSuffix: true,
                      },
                    )}`
                  : "No messages yet"}
              </span>
            </div>
            {participantCountSummary && (
              <>
                <Separator
                  orientation="vertical"
                  className="hidden h-4 md:block"
                />
                <span>Participants: {participantCountSummary}</span>
              </>
            )}
          </div>
        </div>
        <AlertDialog open={showStopDialog} onOpenChange={setShowStopDialog}>
          <AlertDialogContent className={CHAT_ALERT_CONTENT_CLASS_NAME}>
            <AlertDialogHeader className={CHAT_DIALOG_HEADER_CLASS_NAME}>
              <AlertDialogTitle>Stop this chat?</AlertDialogTitle>
              <AlertDialogDescription
                className={CHAT_DIALOG_DESCRIPTION_CLASS_NAME}
              >
                This will permanently stop the conversation. This action cannot
                be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className={CHAT_DIALOG_FOOTER_CLASS_NAME}>
              <AlertDialogCancel disabled={deleteChatMutation.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="border-red-600 bg-red-600 text-white hover:border-red-700 hover:bg-red-700"
                onClick={() =>
                  deleteChatMutation.mutate({
                    threadId: thread.id,
                  })
                }
                disabled={deleteChatMutation.isPending}
              >
                {deleteChatMutation.isPending ? "Stopping..." : "Stop Chat"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
